import "./MatchStats.css";

import { t } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { SoccerMatchStatType, SoccerMatchStatus } from "../../../../types";
import { useMatchStats } from "../../hooks/useMatchStats";
import { useFixtureScore } from "../../stores";
import { DataLoader } from "../DataLoader";
import { ErrorScreen } from "../ErrorScreen";
import { NoDataAvailable } from "../NoDataAvailable";
import { StatMeter } from "../StatMeter";

interface StatItem {
  label: string;
  key: SoccerMatchStatType;
  scoreLabel?: string;
  isStandaloneStat?: boolean;
}

interface StatSection {
  title: string;
  stats: StatItem[];
}

const statSections: StatSection[] = [
  {
    title: t("statsTab.sections.attack"),
    stats: [
      { label: t("statsTab.statsList.shots"), key: SoccerMatchStatType.SHOTS },
      { label: t("statsTab.statsList.shotsOnTarget"), key: SoccerMatchStatType.SHOTS_ON_TARGET },
      { label: t("statsTab.statsList.shotsInsideBox"), key: SoccerMatchStatType.SHOTS_INSIDE_BOX },
      { label: t("statsTab.statsList.corners"), key: SoccerMatchStatType.CORNERS },
    ],
  },
  {
    title: t("statsTab.sections.possession"),
    stats: [
      { label: t("statsTab.statsList.ballPossession"), key: SoccerMatchStatType.BALL_POSSESSION, scoreLabel: "%" },
      { label: t("statsTab.statsList.passingAccuracy"), key: SoccerMatchStatType.PASSING_ACCURACY, scoreLabel: "%", isStandaloneStat: true },
      { label: t("statsTab.statsList.passesAttempted"), key: SoccerMatchStatType.TOTAL_PASS },
      { label: t("statsTab.statsList.passesCompleted"), key: SoccerMatchStatType.ACCURATE_PASS },
    ],
  },
  {
    title: t("statsTab.sections.defence"),
    stats: [
      { label: t("statsTab.statsList.tacklesWon"), key: SoccerMatchStatType.WON_TACKLE },
      { label: t("statsTab.statsList.blocks"), key: SoccerMatchStatType.BLOCKED_SCORING_ATT },
      { label: t("statsTab.statsList.interceptions"), key: SoccerMatchStatType.INTERCEPTIONS },
      { label: t("statsTab.statsList.clearances"), key: SoccerMatchStatType.TOTAL_CLEARANCE },
      { label: t("statsTab.statsList.saves"), key: SoccerMatchStatType.SAVES },
    ],
  },
  {
    title: t("statsTab.sections.discipline"),
    stats: [
      { label: t("statsTab.statsList.redCards"), key: SoccerMatchStatType.RED_CARD },
      { label: t("statsTab.statsList.yellowCards"), key: SoccerMatchStatType.YELLOW_CARD },
      { label: t("statsTab.statsList.fouls"), key: SoccerMatchStatType.FOULS },
      { label: t("statsTab.statsList.offsides"), key: SoccerMatchStatType.OFFSIDE },
    ],
  },
];

function StatsSection({
  section,
  teamAstats,
  teamBstats,
}: {
  section: StatSection;
  teamAstats: Record<string, number>;
  teamBstats: Record<string, number>;
}) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="match-stats-section">
      <div
        className="match-stats-section-header"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="match-stats-section-title">{section.title}</span>
        <img
          className={`match-stats-section-arrow ${isOpen ? "" : "collapsed"}`}
          src="/images/arrow-right-white.svg"
          alt=""
        />
      </div>
      {isOpen && (
        <div className="match-stats-section-cards">
          {section.stats.map((stat) => (
            <StatMeter
              key={stat.key}
              label={stat.label}
              scoreLabel={stat.scoreLabel ?? ""}
              teamAData={{
                score: teamAstats[stat.key] ?? 0,
                color: "",
              }}
              teamBData={{
                score: teamBstats[stat.key] ?? 0,
                color: "",
              }}
              isStandaloneStat={stat.isStandaloneStat ?? false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function MatchStats() {
  const { t } = useTranslation();
  const { matchId, matchStatus, kickoffTime } = useFixtureScore(
    useShallow(({ match, matchStatus }) => ({
      matchId: match?.id,
      matchStatus,
      kickoffTime: match?.matchDateTime,
    }))
  );

  const { data, error, isLoading, refetch } = useMatchStats(
    matchId,
    kickoffTime,
  );

  if (isLoading) {
    return (
      <DataLoader
        customStyle={{
          height: "90dvh",
        }}
      />
    );
  }

  if (error) {
    return (
      <div className="match-stats-error">
        <ErrorScreen refetch={refetch} />
      </div>
    );
  }

  if (
    matchStatus !== SoccerMatchStatus.PLAYING &&
    matchStatus !== SoccerMatchStatus.PLAYED
  ) {
    return (
      <div className="match-stats-not-available">
        <NoDataAvailable
          title={t("errorState.noDataAvailable.title")}
          description={t("errorState.noDataAvailable.matchStatDescription")}
        />
      </div>
    );
  }

  const teamAstats = data.teamA;
  const teamBstats = data.teamB;

  return (
    <div className="match-stats-container">
      <div className="match-stats-block">
        {statSections.map((section) => (
          <StatsSection
            key={section.title}
            section={section}
            teamAstats={teamAstats}
            teamBstats={teamBstats}
          />
        ))}
      </div>
    </div>
  );
}
